import { useCallback, useMemo } from "react";

import useToast from "./useToast";

import { useAdminStore } from "../contexts";

import { useAdminApiService } from "../services";
import type { BackendEntityType, PaginationParamsModel } from "../types";
import type { AdminEntityActions, QuickLinkModel } from "../types/AdminTypes";

const useAdmin = () => {
	const adminService = useAdminApiService();

	const store = useAdminStore();
	const { setState, setInitial } = useAdminStore();

	const { showToast } = useToast();

	const methods = useMemo(() => {
		return {
			listUsers: async ({ search, before, after }: { search?: string } & PaginationParamsModel) => {
				setState({ listingUsers: true });
				try {
					if (!after) {
						setState({ people: [], listPeopleNextToken: null });
					}

					const { nextToken, people } = await adminService.listUsers({ search, before, after });
					setState(ctx => ({
						listPeopleNextToken: nextToken,
						people: !!before || !!after ? ctx.people : people
					}));

					return {
						nextToken,
						people
					};
				} catch (err) {
					showToast({
						text: "Error listing users"
					});
					console.error("Error: ", err);
					return {
						nextToken: null,
						people: []
					};
				} finally {
					setState({ listingUsers: false });
				}
			},
			assignUserRoles: async (admins: { add?: { id: string }[]; remove?: { id: string }[] }) => {
				setState({ assigningUserRoles: true });
				try {
					await adminService.assignUserRoles(admins);
				} catch (err) {
					showToast({
						text: "Error assigning user roles"
					});
					console.error("Error: ", err);
				} finally {
					setState({ assigningUserRoles: false });
				}
			},
			listReportedUsers: async ({ after, before, ...rest }: PaginationParamsModel) => {
				setState({ listingReportedUsers: true });
				try {
					if (!after) {
						setState({ reportedPeople: [], listReportedPeopleNextToken: null });
					}

					const { nextToken, people } = await adminService.listReportedUsers({ after, before, ...rest });
					setState(ctx => ({
						listReportedPeopleNextToken: nextToken,
						reportedPeople: !!before || !!after ? ctx.reportedPeople : people
					}));

					return {
						nextToken,
						people
					};
				} catch (err) {
					showToast({
						text: "Error listing reported users"
					});
					console.error("Error: ", err);
					return {
						nextToken: null,
						people: []
					};
				} finally {
					setState({ listingReportedUsers: false });
				}
			},
			banUsers: async (personIds: string[]) => {
				setState({ banningUsers: true });
				try {
					await adminService.banUsers(personIds);
				} catch (err) {
					showToast({
						text: "Error ban users"
					});
					console.error("Error: ", err);
				} finally {
					setState({ banningUsers: false });
				}
			},
			ignoreReportedUsers: async (personIds: string[]) => {
				setState({ ignoringReportedUsers: true });
				try {
					await adminService.ignoreReportedUsers(personIds);
				} catch (err) {
					showToast({
						text: "Error ignoring reported users"
					});
					console.error("Error: ", err);
				} finally {
					setState({ ignoringReportedUsers: false });
				}
			},
			listReportedPosts: async ({ after, before, ...rest }: PaginationParamsModel) => {
				setState({ listingReportedPosts: true });
				try {
					if (!after) {
						setState({ reportedPosts: [], listReportedPostsNextToken: null });
					}

					const { nextToken, posts } = await adminService.listReportedPosts({ after, before, ...rest });
					setState(ctx => ({
						listReportedPostsNextToken: nextToken,
						reportedPosts: !!before || !!after ? ctx.reportedPosts : posts
					}));

					return {
						nextToken,
						posts
					};
				} catch (err) {
					showToast({
						text: "Error listing reported posts"
					});
					console.error("Error: ", err);
					return {
						nextToken: null,
						posts: []
					};
				} finally {
					setState({ listingReportedPosts: false });
				}
			},
			deleteReportedPosts: async (postIds: string[]) => {
				setState({ deletingReportedPosts: true });
				try {
					await adminService.deleteReportedPosts(postIds);
				} catch (err) {
					showToast({
						text: "Error deleting reported posts"
					});
					console.error("Error: ", err);
				} finally {
					setState({ deletingReportedPosts: false });
				}
			},
			ignoreReportedPosts: async (postIds: string[]) => {
				setState({ ignoringReportedPosts: true });
				try {
					await adminService.ignoreReportedPosts(postIds);
				} catch (err) {
					showToast({
						text: "Error ignoring reported posts"
					});
					console.error("Error: ", err);
				} finally {
					setState({ ignoringReportedPosts: false });
				}
			},
			listClubs: async ({ search, after, before }: { search?: string } & PaginationParamsModel) => {
				setState({ loadingClubs: true });
				try {
					if (!after) {
						setState({ clubs: [], listClubNextToken: null });
					}

					const { nextToken, previousToken, clubs } = await adminService.listClubs({ search, before, after });
					setState(ctx => ({
						listClubNextToken: nextToken,
						listClubPreviousToken: previousToken,
						clubs: !!before || !!after ? ctx.clubs : clubs
					}));

					return {
						clubs,
						nextToken,
						previousToken
					};
				} catch (err) {
					showToast({
						text: "Error listing clubs"
					});
					console.error("Error: ", err);
					return {
						clubs: [],
						nextToken: null,
						previousToken: null
					};
				} finally {
					setState({ loadingClubs: false });
				}
			},
			manageClub: async (clubId: string, action: AdminEntityActions) => {
				setState({ submitting: true });
				try {
					const { success } = await adminService.manageClub(clubId, action);
					return success;
				} catch (err) {
					showToast({
						text: "Can't manage club"
					});
					console.error("Error: ", err);
					return false;
				} finally {
					setState({ submitting: false });
				}
			},
			listLeagues: async ({ search, before, after }: { search?: string } & PaginationParamsModel) => {
				setState({ loadingLeagues: true });
				try {
					if (!after) {
						setState({ leagues: [], listLeagueNextToken: null });
					}

					const { nextToken, previousToken, leagues } = await adminService.listLeagues({ search, before, after });
					setState(ctx => ({
						listLeagueNextToken: nextToken,
						listLeaguePreviousToken: previousToken,
						leagues: !!before || !!after ? ctx.leagues : leagues
					}));

					return {
						leagues,
						nextToken,
						previousToken
					};
				} catch (err) {
					showToast({
						text: "Error listing leagues"
					});
					console.error("Error: ", err);
					return {
						leagues: [],
						nextToken: null,
						previousToken: null
					};
				} finally {
					setState({ loadingLeagues: false });
				}
			},
			manageLeague: async (leagueId: string, action: AdminEntityActions) => {
				setState({ submitting: true });
				try {
					const { success } = await adminService.manageLeague(leagueId, action);
					return success;
				} catch (err) {
					showToast({
						text: "Can't manage league"
					});
					console.error("Error: ", err);
					return false;
				} finally {
					setState({ submitting: false });
				}
			},
			listFacilities: async ({ search, before, after }: { search?: string } & PaginationParamsModel) => {
				setState({ loadingFacilities: true });
				try {
					if (!after) {
						setState({ facilities: [], listFacilityNextToken: null });
					}

					const { nextToken, previousToken, facilities } = await adminService.listFacilities({ search, before, after });
					setState(ctx => ({
						listFacilityNextToken: nextToken,
						listFacilityPreviousToken: previousToken,
						facilities: !!before || !!after ? ctx.facilities : facilities
					}));

					return {
						facilities,
						nextToken,
						previousToken
					};
				} catch (err) {
					showToast({
						text: "Error listing facilities"
					});
					console.error("Error: ", err);
					return {
						facilities: [],
						nextToken: null,
						previousToken: null
					};
				} finally {
					setState({ loadingFacilities: false });
				}
			},
			manageFacility: async (facilityId: string, action: AdminEntityActions) => {
				setState({ submitting: true });
				try {
					const { success } = await adminService.manageFacility(facilityId, action);
					return success;
				} catch (err) {
					showToast({
						text: "Can't manage facility"
					});
					console.error("Error: ", err);
					return false;
				} finally {
					setState({ submitting: false });
				}
			},
			listRefOrgs: async ({ search, before, after }: { search?: string } & PaginationParamsModel) => {
				setState({ loadingRefOrgs: true });
				try {
					if (!after) {
						setState({ refOrgs: [], listRefOrgNextToken: null });
					}

					const { nextToken, previousToken, refOrgs } = await adminService.listRefOrgs({ search, before, after });
					setState(ctx => ({
						listRefOrgNextToken: nextToken,
						listRefOrgPreviousToken: previousToken,
						refOrgs: !!before || !!after ? ctx.refOrgs : refOrgs
					}));

					return {
						refOrgs,
						nextToken,
						previousToken
					};
				} catch (err) {
					showToast({
						text: "Error listing referee organizations"
					});
					console.error("Error: ", err);
					return {
						refOrgs: [],
						nextToken: null,
						previousToken: null
					};
				} finally {
					setState({ loadingRefOrgs: false });
				}
			},
			manageRefOrg: async (refOrgId: string, action: AdminEntityActions) => {
				setState({ submitting: true });
				try {
					const { success } = await adminService.manageRefOrg(refOrgId, action);
					return success;
				} catch (err) {
					showToast({
						text: "Can't manage referee organization"
					});
					console.error("Error: ", err);
					return false;
				} finally {
					setState({ submitting: false });
				}
			},
			listQuickLinks: async () => {
				setState({ loadingQuickLinks: true });
				try {
					const { quickLinks } = await adminService.listQuickLinks();
					setState({ quickLinks });

					return quickLinks;
				} catch (err) {
					showToast({
						text: "Error listing quick links"
					});
					console.error("Error: ", err);
					return [];
				} finally {
					setState({ loadingQuickLinks: false });
				}
			},
			updateQuickLinks: async (params: {
				add?: Partial<QuickLinkModel>[];
				remove?: { id: string }[];
				reorder?: { id: string; newOrder: number };
			}) => {
				setState({ submitting: true });
				try {
					const { success } = await adminService.updateQuickLinks(params);
					return success;
				} catch (err) {
					showToast({
						text: "Can't update quick links"
					});
					console.error("Error: ", err);
					return false;
				} finally {
					setState({ submitting: false });
				}
			},
			listPinnedPosts: async () => {
				setState({ loadingPinnedPosts: true });
				try {
					const { posts } = await adminService.listPinnedPosts();
					setState({ pinnedPosts: posts });

					return posts;
				} catch (err) {
					showToast({
						text: "Error listing pinned posts"
					});
					console.error("Error: ", err);
					return [];
				} finally {
					setState({ loadingPinnedPosts: false });
				}
			},
			updatePinnedPosts: async (params: { add?: { id: string }[]; remove?: { id: string }[] }) => {
				setState({ submitting: true });
				try {
					const { success } = await adminService.updatePinnedPosts(params);
					return success;
				} catch (err) {
					showToast({
						text: "Can't update pinned posts"
					});
					console.error("Error: ", err);
					return false;
				} finally {
					setState({ submitting: false });
				}
			},
			createPushNotification: async (params: {
				title: string;
				text: string;
				entityId: string;
				entityType: BackendEntityType;
			}) => {
				setState({ submitting: true });
				try {
					const { success } = await adminService.createPushNotification(params);
					return success;
				} catch (err) {
					showToast({
						text: "Can't send a push notification"
					});
					console.error("Error: ", err);
					return false;
				} finally {
					setState({ submitting: false });
				}
			},
			listCronJobs: async () => {
				setState({ loadingCronJobs: true });
				try {
					const { crons } = await adminService.listCronJobs();
					setState({ cronJobs: crons });

					return crons;
				} catch (err) {
					showToast({
						text: "Error listing cron jobs"
					});
					console.error("Error: ", err);
					return [];
				} finally {
					setState({ loadingCronJobs: false });
				}
			},
			runCronNow: async (cronId: string) => {
				setState({ submitting: true });
				try {
					const { success } = await adminService.runCronNow(cronId);
					return success;
				} catch (err) {
					showToast({
						text: "Can't run cron job"
					});
					console.error("Error: ", err);
					return false;
				} finally {
					setState({ submitting: false });
				}
			},
			restartCron: async (cronId: string) => {
				setState({ submitting: true });
				try {
					const { success } = await adminService.restartCron(cronId);
					return success;
				} catch (err) {
					showToast({
						text: "Can't restart cron job"
					});
					console.error("Error: ", err);
					return false;
				} finally {
					setState({ submitting: false });
				}
			},
			resetStore: () => {
				setInitial();
			}
		};
	}, [setInitial, setState, showToast, adminService]);

	const getData = useCallback(() => {
		return store;
	}, [store]);

	return { ...methods, getData };
};

export default useAdmin;
